/* eslint-disable prefer-destructuring */
import React, { useState } from 'react';
import { useGate, useStoreMap } from 'effector-react';
import { toFlag } from 'pano-utils';
import type { SortEnd } from 'react-sortable-hoc';
import { useNavigate } from 'react-router-dom';
import { $tourData, $tourDataApi } from '@stores/dataStores';
import useTourEditing from '@hooks/useTourEditing';
import { $GateSandTable, $GateSandTableId } from '@stores/editingStores';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import Tip from '@components/Tip';
import Button from '@components/Button';
import FeatureToggle from '@components/FeatureToggle';
import SortableList from '@components/SortableList';
import arrayMove from '@utils/arrayMove';
import PanelBase from '../PanelBase';
import { SandTableListItem } from '../../sandTable/components/SandTableListItem';
import { showSandTableModal } from '../../sandTable/SandTableModal';

const MAX = 10;

export function PanelEnterSandTable() {
  const defaultValue = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state.config?.sandBox,
  });
  const { state, setState, isChanged } = useTourEditing(defaultValue, {
    list: [],
    showWhenLoad: 1,
  });
  const [selectedId, setSelectedId] = useState<string | null>(
    state?.defaultId ?? state?.list?.[0] ?? null
  );

  const navigate = useNavigate();

  useGate($GateSandTable, state);
  useGate($GateSandTableId, selectedId);

  const { list = [], showWhenLoad, defaultId } = state;

  const handleConfirm = () => {
    $tourDataApi.update((draft) => {
      draft.config.sandBox = state;
    });
  };

  const handleSortEnd = (sort: SortEnd) => {
    setState((draft) => {
      draft.list = arrayMove({
        array: draft.list ?? [],
        fromIndex: sort.oldIndex,
        toIndex: sort.newIndex,
      });
    });
  };

  const handleAddClick = async () => {
    const { idList } = await showSandTableModal({
      max: MAX,
      selected: list,
      navigate,
    });
    if (!idList) return;
    setState((draft) => {
      if (!idList.includes(defaultId!)) {
        draft.defaultId = idList[0];
      }
      draft.list = idList;
    });
    if (!idList.includes(selectedId!)) {
      setSelectedId(idList[0] ?? null);
    }
  };

  const handleItemClick = (id: string) => {
    setSelectedId(id);
  };
  const handleItemDelete = (id: string) => {
    const newList = list.filter((item) => item !== id);
    setState((draft) => {
      if (id === defaultId) {
        draft.defaultId = newList[0];
      }
      draft.list = newList;
    });
    if (id === selectedId) {
      setSelectedId(newList[0]);
    }
  };
  const handleItemSetDefault = (id: string) => {
    setState((draft) => {
      draft.defaultId = id;
    });
  };

  const renderList = () => {
    return (
      <SortableList
        axis="y"
        lockAxis="y"
        distance={10}
        onSortEnd={handleSortEnd}
      >
        <div className="mt-20">
          {list.map((stId, index) => {
            return (
              <SandTableListItem
                key={stId}
                id={stId}
                index={index}
                isSelected={stId === selectedId}
                isDefault={stId === state?.defaultId}
                className="bg-black-#34 hover:bg-black-#45"
                onClick={handleItemClick}
                onDeleteClick={handleItemDelete}
                onSetDefaultClick={handleItemSetDefault}
              />
            );
          })}
        </div>
      </SortableList>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      onConfirm={handleConfirm}
      title={<Title>开场沙盘</Title>}
    >
      <Collapse
        title={
          <div className="flex items-center">
            沙盘列表<Tip className="ml-5">{`${list.length}/${MAX}`}</Tip>
          </div>
        }
      >
        <Button
          className="w-full"
          disabled={list.length >= MAX}
          onClick={handleAddClick}
        >
          添加沙盘
        </Button>
        {list.length ? (
          renderList()
        ) : (
          <div className="mt-10 flex flex-col items-center">
            <Tip>最多可添加10个沙盘</Tip>
            <Tip>添加后支持上下拖拽调整沙盘顺序</Tip>
          </div>
        )}
      </Collapse>
      <Collapse title="沙盘展示">
        <FeatureToggle
          title="打开作品时显示开场沙盘"
          isOn={!!showWhenLoad}
          onToggle={(isOn) =>
            setState((draft) => {
              draft.showWhenLoad = toFlag(isOn);
            })
          }
        />
      </Collapse>
    </PanelBase>
  );
}
